import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FiArrowLeft, FiNavigation, FiMapPin, FiCalendar, FiDollarSign, FiUser, FiBriefcase, FiGlobe, FiTag } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTripByIdRequest } from '../../store/actions/tripsActions';
import { handleApiError } from '../../utils/errorHandler';
import './AdminTripDetail.css';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminTripDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { tripId } = useParams();
    const { selectedTrip } = useSelector((state) => state.trips);

    useEffect(() => {
        if (tripId) {
            dispatch(fetchTripByIdRequest(tripId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripId]);

    const activity = selectedTrip?.data?.activity || selectedTrip?.data || null;
    const partnerInfo = activity?.partner_info || {};
    const references = activity?.references || [];

    if (selectedTrip.loading) {
        return (
            <div className="dashboard-page admin-trip-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du séjour</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement du séjour...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (selectedTrip.error) {
        return (
            <div className="dashboard-page admin-trip-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du séjour</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">{selectedTrip.error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!activity) {
        return (
            <div className="dashboard-page admin-trip-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du séjour</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de séjourdisponible pour l'identifiant: {tripId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-trip-detail-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiNavigation className="me-2" />
                    Détail du séjour
                </h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            <Section title="Informations principales">
                <Row>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiNavigation className="me-1" />
                            Nom
                        </Label>
                        <Value>{activity.name || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiTag className="me-1" />
                            Catégorie
                        </Label>
                        <Value>{activity.category || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiDollarSign className="me-1" />
                            Prix
                        </Label>
                        <Value>{activity.activity_price || 'N/A'}</Value>
                    </Col>
                    {activity.new_trip_price && (
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiDollarSign className="me-1" />
                                Nouveau Prix
                            </Label>
                            <Value style={{ color: 'var(--attijari-primary)' }}>{activity.new_trip_price}</Value>
                        </Col>
                    )}
                    <Col md={6} className="mb-3">
                        <Label>Date modifiée</Label>
                        <Value>
                            {activity.trip_modified ? (
                                <Badge bg="warning">Oui</Badge>
                            ) : (
                                <Badge bg="secondary">Non</Badge>
                            )}
                        </Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiNavigation className="me-1" />
                            Code Activité
                        </Label>
                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {activity.activity_code || 'N/A'}
                        </Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Durée</Label>
                        <Value>{activity.duration || 'N/A'}</Value>
                    </Col>
                </Row>
            </Section>

            <Section title="Dates du séjour">
                <Row>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiCalendar className="me-1" />
                            Date de début
                        </Label>
                        <Value>{activity.activity_start_date || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiCalendar className="me-1" />
                            Date de fin
                        </Label>
                        <Value>{activity.activity_end_date || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Heure de début</Label>
                        <Value>{activity.activity_start_time || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Heure de fin</Label>
                        <Value>{activity.activity_end_time || 'N/A'}</Value>
                    </Col>
                </Row>
            </Section>

            <Section title="Dates effectives">
                {/* Dates effectives actuelles */}
                <Row>
                    <Col md={12}>
                        <h6 className="text-muted mb-2">Dates effectives actuelles</h6>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiCalendar className="me-1" />
                            Date effective de début
                        </Label>
                        <Value>{activity.trip_effective_start_date || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiCalendar className="me-1" />
                            Date effective de fin
                        </Label>
                        <Value>{activity.trip_effective_end_date || 'N/A'}</Value>
                    </Col>
                </Row>

                {/* Nouvelles dates effectives */}
                <Row className="mt-3">
                    <Col md={12}>
                        <h6 className="text-primary mb-2">Nouvelles dates effectives</h6>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiCalendar className="me-1" />
                            Nouvelle date effective de début
                        </Label>
                        <Value style={{ color: activity.new_trip_effective_start_date ? 'var(--attijari-primary)' : 'inherit' }}>
                            {activity.new_trip_effective_start_date || 'N/A'}
                        </Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiCalendar className="me-1" />
                            Nouvelle date effective de fin
                        </Label>
                        <Value style={{ color: activity.new_trip_effective_end_date ? 'var(--attijari-primary)' : 'inherit' }}>
                            {activity.new_trip_effective_end_date || 'N/A'}
                        </Value>
                    </Col>
                </Row>

                {/* Nouvelles dates de séjour */}
                <Row className="mt-3">
                    <Col md={12}>
                        <h6 className="text-primary mb-2">Nouvelles dates de séjour</h6>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiCalendar className="me-1" />
                            Nouvelle date de début
                        </Label>
                        <Value style={{ color: activity.new_trip_start_date ? 'var(--attijari-primary)' : 'inherit' }}>
                            {activity.new_trip_start_date || 'N/A'}
                        </Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiCalendar className="me-1" />
                            Nouvelle date de fin
                        </Label>
                        <Value style={{ color: activity.new_trip_end_date ? 'var(--attijari-primary)' : 'inherit' }}>
                            {activity.new_trip_end_date || 'N/A'}
                        </Value>
                    </Col>
                </Row>

                {/* Informations de modification */}
                {activity.trip_modified && (
                    <Row className="mt-3">
                        <Col md={12}>
                            <h6 className="text-warning mb-2">⚠️ Dates modifiées</h6>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>Date de modification</Label>
                            <Value>
                                <Badge bg="warning">Les dates ont été modifiées</Badge>
                            </Value>
                        </Col>
                    </Row>
                )}
            </Section>

            <Section title="Localisation">
                <Row>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiMapPin className="me-1" />
                            Adresse
                        </Label>
                        <Value>{activity.activity_address || 'N/A'}</Value>
                    </Col>
                    <Col md={4} className="mb-3">
                        <Label>Ville</Label>
                        <Value>{activity.activity_city || 'N/A'}</Value>
                    </Col>
                    <Col md={4} className="mb-3">
                        <Label>Code Postal</Label>
                        <Value>{activity.activity_zip_code || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>
                            <FiGlobe className="me-1" />
                            Pays
                        </Label>
                        <Value>{activity.activity_country || 'N/A'}</Value>
                    </Col>
                    <Col md={4} className="mb-3">
                        <Label>Latitude</Label>
                        <Value>{activity.latitude || 'N/A'}</Value>
                    </Col>
                    <Col md={4} className="mb-3">
                        <Label>Longitude</Label>
                        <Value>{activity.longitude || 'N/A'}</Value>
                    </Col>
                </Row>
            </Section>

            {partnerInfo && Object.keys(partnerInfo).length > 0 && (
                <Section title="Informations du Partenaire">
                    <Row>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiUser className="me-1" />
                                Prénom
                            </Label>
                            <Value>{partnerInfo.first_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiUser className="me-1" />
                                Nom
                            </Label>
                            <Value>{partnerInfo.last_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiBriefcase className="me-1" />
                                Entreprise
                            </Label>
                            <Value>{partnerInfo.company_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>Code Utilisateur</Label>
                            <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                {partnerInfo.user_code || 'N/A'}
                            </Value>
                        </Col>
                    </Row>
                </Section>
            )}

            {references && references.length > 0 && (
                <Section title="Références">
                    {references.map((ref, idx) => (
                        <Card key={ref.reference_id || idx} className="mb-3" style={{ borderLeft: '4px solid var(--attijari-primary)' }}>
                            <Card.Body>
                                <Row>
                                    <Col md={6} className="mb-2">
                                        <Label>Nom de la référence</Label>
                                        <Value>{ref.name || 'N/A'}</Value>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <Label>Code référence</Label>
                                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                            {ref.reference_code || 'N/A'}
                                        </Value>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <Label>Catégorie</Label>
                                        <Value>{ref.category || 'N/A'}</Value>
                                    </Col>
                                    {ref.subcoverages && ref.subcoverages.length > 0 && (
                                        <Col md={12} className="mt-3">
                                            <Label>Sous-couvertures ({ref.subcoverages.length})</Label>
                                            {ref.subcoverages.map((sub, subIdx) => (
                                                <Card key={sub.subcoverage_trip_id || subIdx} className="mt-2" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                                                    <Card.Body>
                                                        <Row>
                                                            <Col md={6}>
                                                                <Label>Code sous-couverture</Label>
                                                                <Value style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                                    {sub.subcoverage_trip_code || 'N/A'}
                                                                </Value>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Label>Type de déclencheur</Label>
                                                                <Value>{sub.trigger_type || 'N/A'}</Value>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Label>Source de données (Maintenu)</Label>
                                                                <Value>{sub.maintain_data_source || 'N/A'}</Value>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Label>Source de données (Annulé)</Label>
                                                                <Value>{sub.cancellation_data_source || 'N/A'}</Value>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                </Section>
            )}
        </div>
    );
};

export default AdminTripDetail;

